import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { FloatingField } from '../../../components/FloatingField';
import { useForgotPassword } from '../api/auth-api';
import { AuthLayout, AuthNotice } from '../components/AuthLayout';
import styles from './auth-forms.module.css';

/**
 * AUTH-02 — forgot password.
 *
 * The confirmation deliberately says "if that address has an account", and says it
 * whether or not one does. F1.03 requires the response to be identical either way,
 * and the wording has to match the promise: a screen that said "check your inbox"
 * would be a screen that confirms an address exists.
 */
export function ForgotPasswordPage(): ReactNode {
  const forgot = useForgotPassword();

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    form.setFocus('email');
  }, [form]);

  const onSubmit = form.handleSubmit(async (values) => {
    await forgot.mutateAsync(values).catch(() => undefined);
  });

  if (forgot.isSuccess) {
    return (
      <AuthLayout
        title="Check your email"
        icon={<MailCheck size={20} aria-hidden />}
        footer={
          <Link to="/login" className={styles.link}>
            Back to sign in
          </Link>
        }
      >
        <AuthNotice tone="info">
          If <strong>{form.getValues('email')}</strong> has an account, a reset link is on its
          way. It works once and expires in 60 minutes.
        </AuthNotice>
        <p className={styles.meta}>
          Nothing arrived? Check the spam folder, then ask your admin — they can send an
          invite to a different address.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to choose a new one."
      footer={
        <Link to="/login" className={styles.link}>
          Back to sign in
        </Link>
      }
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        <FloatingField
          {...form.register('email')}
          label="Email"
          error={form.formState.errors.email?.message}
          isRequired
          type="email"
          autoComplete="username"
          inputMode="email"
          placeholder="you@nestgroup.com"
        />

        <div className={styles.actions}>
          <Button type="submit" variant="solid" size="lg" block isPending={forgot.isPending}>
            Send the link
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
